import { profileDisplayName } from "@/stores/documents/repository/mappers";
import { attachDocumentBreadcrumbs } from "@/stores/pm/repository";
import type { UserAvatarUser } from "@/components/ui/user-avatar";
import type { LinkedDocument } from "@/domains/pm/types";
import type { BriefAppendix, BriefAppendixReply } from "../types";
import { client, raise } from "./client";

async function fetchAuthors(ids: number[]): Promise<Map<number, UserAvatarUser>> {
  const authors = new Map<number, UserAvatarUser>();
  if (ids.length === 0) return authors;
  const { data, error } = await client()
    .from("user_profiles")
    .select("id, first_name, last_name, email, avatar_url, color")
    .in("id", ids);
  if (error) raise(error);
  for (const row of (data ?? []) as UserAvatarUser[]) authors.set(row.id, row);
  return authors;
}

/** PostgREST's row cap; the thread tables are paged past it rather than truncated. */
const PAGE_SIZE = 1000;
/** Ids per `.in()` — keeps the request URL well short of proxy limits. */
const IN_CHUNK_SIZE = 200;

interface ReplyRow {
  id: number;
  appendix_id: number;
  author_id: number;
  body: string;
  created_at: string;
}

interface AppendixDocumentRow {
  id: number;
  appendix_id: number;
  reply_id: number | null;
  document_id: number;
}

interface AppendixRow {
  id: number;
  brief_id: number;
  author_kind: BriefAppendix["author_kind"];
  author_id: number | null;
  confirmed_by: number | null;
  source_finding_id: number | null;
  title: string;
  body: string;
  created_at: string;
  modified_at: string;
  modified_by: number | null;
  filed_issue_id: number | null;
}

/** Every row of a brief-scoped thread table, oldest first, paged by id. */
async function fetchAllForBrief<T extends { id: number }>(
  table: string,
  columns: string,
  briefId: number
): Promise<T[]> {
  const rows: T[] = [];
  let after = 0;
  for (;;) {
    const { data, error } = await client()
      .from(table)
      .select(columns)
      .eq("brief_id", briefId)
      .gt("id", after)
      .order("id", { ascending: true })
      .limit(PAGE_SIZE);
    if (error) raise(error);
    const page = (data ?? []) as unknown as T[];
    rows.push(...page);
    if (page.length < PAGE_SIZE) return rows;
    after = page[page.length - 1].id;
  }
}

const LINKED_DOCUMENT_COLUMNS =
  "id, filename, state, content_type, file_path, size, created_at, parent_document_id, version, project_id, folder_id";

async function fetchLinkedDocuments(ids: number[]): Promise<Map<number, LinkedDocument>> {
  const docs = new Map<number, LinkedDocument>();
  for (let i = 0; i < ids.length; i += IN_CHUNK_SIZE) {
    const { data, error } = await client()
      .from("documents")
      .select(LINKED_DOCUMENT_COLUMNS)
      .in("id", ids.slice(i, i + IN_CHUNK_SIZE))
      .eq("is_deleted", false);
    if (error) raise(error);
    for (const doc of (data ?? []) as unknown as LinkedDocument[]) docs.set(doc.id, doc);
  }
  // The folder-path tooltip on each chip, as on issue comments.
  await attachDocumentBreadcrumbs([...docs.values()]);
  return docs;
}

const APPENDIX_COLUMNS =
  "id, brief_id, author_kind, author_id, confirmed_by, source_finding_id, title, body, " +
  "created_at, modified_at, modified_by, filed_issue_id";

function groupAppendixDocuments(
  linkRows: AppendixDocumentRow[],
  docs: Map<number, LinkedDocument>
) {
  // Attachments keyed by appendix (reply_id null) or by reply, in attach order.
  const appendixDocs = new Map<number, LinkedDocument[]>();
  const replyDocs = new Map<number, LinkedDocument[]>();
  for (const link of linkRows) {
    const doc = docs.get(link.document_id);
    if (!doc) continue;
    const bucket = link.reply_id === null ? appendixDocs : replyDocs;
    const key = link.reply_id ?? link.appendix_id;
    bucket.set(key, [...(bucket.get(key) ?? []), doc]);
  }

  return { appendixDocs, replyDocs };
}

function groupAppendixReplies(
  replyRows: ReplyRow[],
  profiles: Map<number, UserAvatarUser>,
  replyDocs: Map<number, LinkedDocument[]>,
  nameOf: (id: number | null) => string | null
) {
  const repliesByAppendix = new Map<number, BriefAppendixReply[]>();
  for (const reply of replyRows) {
    const list = repliesByAppendix.get(reply.appendix_id) ?? [];
    list.push({
      ...reply,
      author: profiles.get(reply.author_id) ?? null,
      author_name: nameOf(reply.author_id),
      documents: replyDocs.get(reply.id) ?? [],
    });
    repliesByAppendix.set(reply.appendix_id, list);
  }

  return repliesByAppendix;
}

/** Every appendix of the brief, oldest first, with its thread and attachments. */
export async function fetchBriefAppendices(briefId: number): Promise<BriefAppendix[]> {
  const [rows, replyRows, linkRows] = await Promise.all([
    fetchAllForBrief<AppendixRow>("project_brief_appendices", APPENDIX_COLUMNS, briefId),
    fetchAllForBrief<ReplyRow>(
      "project_brief_appendix_replies",
      "id, appendix_id, author_id, body, created_at",
      briefId
    ),
    // RLS returns only links to documents the caller can see.
    fetchAllForBrief<AppendixDocumentRow>(
      "project_brief_appendix_documents",
      "id, appendix_id, reply_id, document_id",
      briefId
    ),
  ]);

  const profileIds = [
    ...new Set(
      [
        ...rows.flatMap((r) => [r.author_id, r.confirmed_by]),
        ...replyRows.map((r) => r.author_id),
      ].filter((id): id is number => id !== null)
    ),
  ];
  const [profiles, docs] = await Promise.all([
    fetchAuthors(profileIds),
    fetchLinkedDocuments([...new Set(linkRows.map((l) => l.document_id))]),
  ]);
  const nameOf = (id: number | null) => {
    const profile = id === null ? undefined : profiles.get(id);
    return profile ? profileDisplayName(profile) : null;
  };

  const { appendixDocs, replyDocs } = groupAppendixDocuments(linkRows, docs);
  const repliesByAppendix = groupAppendixReplies(replyRows, profiles, replyDocs, nameOf);

  return rows
    .map((row) => ({
      ...row,
      author: row.author_id === null ? null : (profiles.get(row.author_id) ?? null),
      author_name: nameOf(row.author_id),
      confirmed_by_name: nameOf(row.confirmed_by),
      documents: appendixDocs.get(row.id) ?? [],
      replies: repliesByAppendix.get(row.id) ?? [],
    }))
    .sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id - b.id);
}

/**
 * Documents the caller cannot see, or from another project, are skipped by
 * the RPC. An appendix needs text or at least one attachment.
 */
export async function addAppendix(
  briefId: number,
  body: string,
  documentIds: number[]
): Promise<void> {
  const { error } = await client().rpc("brief_add_appendix", {
    p_brief_id: briefId,
    p_body: body,
    p_document_ids: documentIds,
  });
  if (error) raise(error);
}

/** Replaces the text and the attachments the editor can see. */
export async function updateAppendix(
  appendixId: number,
  body: string,
  documentIds: number[]
): Promise<void> {
  const { error } = await client().rpc("brief_update_appendix", {
    p_appendix_id: appendixId,
    p_body: body,
    p_document_ids: documentIds,
  });
  if (error) raise(error);
}

/** Deletes an appendix with its thread. A confirmed version keeps its copy. */
export async function deleteAppendix(appendixId: number): Promise<void> {
  const { error } = await client().rpc("brief_delete_appendix", { p_appendix_id: appendixId });
  if (error) raise(error);
}

export async function replyToAppendix(
  appendixId: number,
  body: string,
  documentIds: number[]
): Promise<void> {
  const { error } = await client().rpc("brief_reply_to_appendix", {
    p_appendix_id: appendixId,
    p_body: body,
    p_document_ids: documentIds,
  });
  if (error) raise(error);
}

export interface LinkedAppendixIssue {
  appendix_id: number;
  issue_id: number;
  issue_number: number;
}

/**
 * Links the issue the PM form just created to the appendix it was created
 * from. Once only; the issue must be a live issue of the brief's project.
 */
export async function linkAppendixIssue(
  appendixId: number,
  issueId: number
): Promise<LinkedAppendixIssue> {
  const { data, error } = await client().rpc("brief_link_appendix_issue", {
    p_appendix_id: appendixId,
    p_issue_id: issueId,
  });
  if (error) raise(error);
  return data as LinkedAppendixIssue;
}
